import React, { useEffect, useState } from 'react';
import {
  Box, Paper, Typography, Stack, Chip, Divider, Table, TableBody, TableCell,
  TableHead, TableRow, TableContainer, Alert, CircularProgress, Tooltip,
} from '@mui/material';
import { httpClient } from '../services/http-client';
import { formatCurrency } from '../utils/currency';
import { useBaseCurrency } from '../stores/currency-store';

interface Loan {
  account_id: string;
  name: string;
  lender?: string;
  balance: string;
  currency: string;
  rate_type?: string;
  rate_basis?: string;
  interest_rate?: string;
  term_start?: string;
  term_end?: string;
}

interface Payment {
  date: string;
  description: string;
  account: string;
  amount: string;
  currency: string;
}

export interface PropertyDetail {
  account_id: string;
  name: string;
  address?: string;
  vin?: string;
  currency: string;
  current_value: string;
  purchase_price?: string;
  purchase_date?: string;
  valued_at?: string;
  valuation_note?: string;
  equity: string;
  loans: Loan[];
  payments: Payment[];
}

const num = (s?: string | null) => (s ? parseFloat(s) : 0);
const day = (s?: string | null) => (s ? new Date(s).toLocaleDateString() : '—');

/** How a rate reads: the basis if variable, otherwise the rate itself. */
function rateLabel(l: Loan): string {
  if (l.rate_basis) return l.rate_basis;
  if (l.interest_rate != null && l.interest_rate !== '') {
    const r = num(l.interest_rate);
    return r === 0 ? '0% financing' : `${r}%`;
  }
  return '—';
}

/** Whole months until a date; negative once it has passed. */
function monthsUntil(iso?: string): number | null {
  if (!iso) return null;
  const end = new Date(iso);
  const now = new Date();
  return (end.getFullYear() - now.getFullYear()) * 12 + (end.getMonth() - now.getMonth());
}

/**
 * What an owned thing is, what is owed on it, and what has been paid.
 *
 * EQUITY IS THE HEADLINE, not market value. "The house is worth $1.75M" and
 * "the house is worth $399k to us" are different statements, and only the
 * second survives contact with the mortgage. Showing value alone is how a
 * household talks itself into feeling richer than it is.
 *
 * PAYMENTS COME FROM THE LOANS, not the asset. A house has no transactions of
 * its own -- it just sits there -- while every mortgage payment is a movement
 * on the debt. Reading the asset would always show nothing and look broken.
 */
export const PropertyPanel: React.FC<{ accountId: string }> = ({ accountId }) => {
  const baseCurrency = useBaseCurrency();
  const [d, setD] = useState<PropertyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    httpClient
      .get<PropertyDetail>(`/accounts/${accountId}/property`)
      .then((r) => { if (!cancelled) setD((r as any)?.data ?? r); })
      .catch((e: any) => { if (!cancelled) setError(e?.message ?? 'Could not load'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [accountId]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={24} /></Box>;
  }
  if (error || !d) return null;

  // Only meaningful for something owned outright or financed. A chequing
  // account has no equity story, so the panel stays out of the way.
  if (d.loans.length === 0 && !d.address && !d.vin && !d.purchase_price) return null;

  const money = (v: string | number, ccy = d.currency) =>
    formatCurrency(v, ccy as any, { base: baseCurrency as any });

  const gain = d.purchase_price ? num(d.current_value) - num(d.purchase_price) : null;

  return (
    <Paper sx={{ p: 2.5, mb: 3 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="overline" color="text.secondary">Estimated value</Typography>
          <Typography variant="h5" fontWeight={700}>{money(d.current_value)}</Typography>
          {d.valued_at && (
            <Tooltip title={d.valuation_note || 'Hand-entered value'}>
              <Typography variant="caption" color="text.secondary">
                as of {day(d.valued_at)}
              </Typography>
            </Tooltip>
          )}
        </Box>

        <Box>
          <Typography variant="overline" color="text.secondary">Your equity</Typography>
          <Typography
            variant="h5"
            fontWeight={700}
            color={num(d.equity) >= 0 ? 'success.main' : 'error.main'}
          >
            {money(d.equity)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            value less everything secured on it
          </Typography>
        </Box>

        {gain !== null && (
          <Box>
            <Typography variant="overline" color="text.secondary">Since purchase</Typography>
            <Typography
              variant="h5"
              fontWeight={700}
              color={gain >= 0 ? 'success.main' : 'error.main'}
            >
              {gain >= 0 ? '+' : ''}{money(gain)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              bought {day(d.purchase_date)} for {money(d.purchase_price!)}
            </Typography>
          </Box>
        )}
      </Stack>

      {(d.address || d.vin) && (
        <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
          {d.address && <Chip size="small" variant="outlined" label={d.address} />}
          {d.vin && <Chip size="small" variant="outlined" label={`VIN ${d.vin}`} />}
        </Stack>
      )}

      {d.loans.length > 0 && (
        <>
          <Divider sx={{ my: 2 }} />
          <Typography variant="overline" color="text.secondary">Secured against this</Typography>
          {d.loans.map((l) => {
            const m = monthsUntil(l.term_end);
            return (
              <Box key={l.account_id} sx={{ py: 1.25 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                  <Typography variant="body2" fontWeight={650}>{l.name}</Typography>
                  {l.lender && (
                    <Typography variant="caption" color="text.secondary">{l.lender}</Typography>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Typography variant="body2" fontWeight={650} color="error.main">
                    {money(l.balance, l.currency)}
                  </Typography>
                </Stack>
                <Stack direction="row" spacing={0.75} sx={{ mt: 0.5, flexWrap: 'wrap', gap: 0.5 }}>
                  <Chip size="small" variant="outlined" sx={{ height: 20 }} label={rateLabel(l)} />
                  {l.rate_type && (
                    <Chip size="small" variant="outlined" sx={{ height: 20 }} label={l.rate_type} />
                  )}
                  {l.term_end && (
                    // A term maturing inside a year is the thing worth
                    // noticing: it is when the rate is renegotiated.
                    <Chip
                      size="small"
                      sx={{ height: 20 }}
                      color={m !== null && m <= 12 ? 'warning' : 'default'}
                      variant={m !== null && m <= 12 ? 'filled' : 'outlined'}
                      label={
                        m !== null && m < 0
                          ? `term ended ${day(l.term_end)}`
                          : `renews ${day(l.term_end)}${m !== null ? ` · ${m} mo` : ''}`
                      }
                    />
                  )}
                </Stack>
              </Box>
            );
          })}
        </>
      )}

      <Divider sx={{ my: 2 }} />
      <Typography variant="overline" color="text.secondary">Payments made</Typography>
      {d.payments.length === 0 ? (
        <Alert severity="info" sx={{ mt: 1 }}>
          No payments recorded against this yet. Payments appear here once the
          lender&rsquo;s transactions sync, or once you import them &mdash; a
          balance alone carries no payment history.
        </Alert>
      ) : (
        <TableContainer sx={{ mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Description</TableCell>
                <TableCell>Account</TableCell>
                <TableCell align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {d.payments.map((p, i) => (
                <TableRow key={`${p.date}-${i}`} hover>
                  <TableCell>{day(p.date)}</TableCell>
                  <TableCell>{p.description}</TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">{p.account}</Typography>
                  </TableCell>
                  <TableCell align="right">{money(p.amount, p.currency)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};
